import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "@/hooks/useAppSelector";
import { Box, Button, Popover, Typography } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { FilterSubCategory } from "@/types/home";
import { addFilter, removeFilter } from "@/store/slices/stepSlice";
import { trackEvent } from "@/lib/gtag";

const CategoryFilter: React.FC = () => {
  const dispatch = useAppDispatch();
  const { subCategories } = useAppSelector((state) => state.home);
  const [anchorEl, setAnchorEl] = useState<
    HTMLButtonElement | HTMLDivElement | null
  >(null);
  const { selectedFilters } = useAppSelector((state) => state.steps);

  const handleOpen = (
    event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>
  ) => {
    setAnchorEl(event.currentTarget);
    trackEvent({
      action: "filter_open",
      category: "category_filter",
      label: "Filter Popover Opened",
    });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFilterSelect = (filter: FilterSubCategory) => {
    if (selectedFilters.some((item) => item.sc_id === filter.sc_id)) {
      dispatch(removeFilter(filter.sc_id));
      trackEvent({
        action: "filter_remove",
        category: "category_filter",
        label: `Removed Filter: ${filter.SubcategorieName}`,
      });
    } else {
      dispatch(addFilter(filter));
      trackEvent({
        action: "filter_apply",
        category: "category_filter",
        label: `Applied Filter: ${filter.SubcategorieName}`,
      });
    }
  };

  return (
    <Box className="relative flex items-center gap-4 filter-section">
      {/* Filter Button */}
      <Button
        variant="outlined"
        onClick={handleOpen}
        sx={{
          textTransform: "none",
          fontWeight: "bold",
          borderColor: selectedFilters.length
            ? "var(--color-peach)"
            : "var(--color-blue)",
          color: selectedFilters.length
            ? "var(--color-peach)"
            : "var(--color-blue)",
          height: "35px",
          display: "flex",
          alignItems: "center",
          gap: 1,
          minWidth: "fit-content",
          "&:hover": {
            borderColor: selectedFilters.length
              ? "var(--color-peach)"
              : "var(--color-blue)",
            backgroundColor: "#f7f7f7",
          },
        }}
      >
        <FilterAltIcon sx={{ fontSize: "18px" }} />
        {selectedFilters.length ? "Filtered By" : "Filters"}
      </Button>

      {/* Selected Filters Summary (Right of Filter Button) */}
      {selectedFilters.length > 0 && (
        <Box className="flex items-center gap-2">
          {selectedFilters.slice(0, 2).map((filter, index) => (
            <Box
              key={index}
              className="bg-gray-200 text-black-1 whitespace-nowrap text-xs font-bold rounded-full px-2 h-[30px] flex items-center justify-center"
            >
              {filter?.SubcategorieName}
            </Box>
          ))}
          {selectedFilters.length > 2 && (
            <Box
              className="bg-blue text-white-1 text-xs font-bold rounded-full w-[25px] h-[25px] flex items-center justify-center cursor-pointer hover:opacity-80"
              onClick={handleOpen}
            >
              {selectedFilters.length}+
            </Box>
          )}
        </Box>
      )}
      {/* Popover for Selecting Filters */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{ mt: 1 }}
      >
        <Box className="min-w-[160px] max-w-[550px] flex flex-wrap gap-5 p-3 bg-white rounded-lg shadow-md">
          {subCategories?.map((item: FilterSubCategory, idx: number) => (
            <Typography
              key={idx}
              className={`cursor-pointer !text-sm !font-[Raleway] ${
                selectedFilters.some((filter) => filter.sc_id === item.sc_id)
                  ? "!font-bold text-blue"
                  : "text-gray-400 !font-semibold"
              } hover:underline`}
              onClick={() => handleFilterSelect(item)}
            >
              {item.SubcategorieName}
            </Typography>
          ))}
        </Box>
      </Popover>
    </Box>
  );
};

export default CategoryFilter;
